import React, { useMemo, useState } from 'react';
import {
    Alert,
    Badge,
    Button,
    Card,
    Col,
    Form,
    Nav,
    Row,
    Table,
} from 'react-bootstrap';
import {
    FiAlertTriangle,
    FiCheckCircle,
    FiDownload,
    FiFilter,
    FiInfo,
    FiMail,
    FiMinusCircle,
    FiSearch,
    FiShield,
    FiUsers,
    FiXCircle,
} from 'react-icons/fi';
import * as XLSX from 'xlsx';
import { useTranslation } from '../../hooks/useTranslation';
import {
    TRIP_CANCELLATION_CODES,
    TRIP_CANCELLATION_PARTNERS,
    TRIP_CANCELLATION_ROW_STATUS,
    buildTripCancellationExcelRows,
    buildTripCancellationStatusCounts,
    filterTripCancellationDetails,
    filterTripCancellationPolicyRows,
    flattenTripCancellationPolicyRows,
    formatTripCancellationDateTime,
    getTripCancellationAlertVariant,
    getTripCancellationStatusFilters,
    isTripCancellationPartialSuccess,
    normalizeTripCancellationResult,
    paginateTripCancellationDetails,
    shouldShowTripCancellationSummary,
} from '../../utils/tripCancellationResult';
import './TriggeredRecapResult.css';
import './TripCancellationResult.css';

const PAGE_SIZES = [10, 20, 50, 100];

const StatCard = ({ icon: Icon, tone, label, value }) => (
    <Card className="triggered-recap-stat-card">
        <Card.Body className="d-flex align-items-center gap-3">
            <span className={`triggered-recap-stat-icon ${tone}`} aria-hidden>
                <Icon />
            </span>
            <div>
                <div className="text-muted small">{label}</div>
                <div className="triggered-recap-stat-value">{value}</div>
            </div>
        </Card.Body>
    </Card>
);

const StatusBadge = ({ status, prefix, t }) => {
    const config = {
        [TRIP_CANCELLATION_ROW_STATUS.PROCESSED]: { bg: 'success', label: t(`${prefix}.rowStatus.processed`) },
        [TRIP_CANCELLATION_ROW_STATUS.SKIPPED_ALREADY_CANCELLED]: { bg: 'secondary', label: t(`${prefix}.rowStatus.skipped_already_cancelled`) },
        [TRIP_CANCELLATION_ROW_STATUS.NOT_FOUND]: { bg: 'warning', text: 'dark', label: t(`${prefix}.rowStatus.not_found`) },
        [TRIP_CANCELLATION_ROW_STATUS.ERROR]: { bg: 'danger', label: t(`${prefix}.rowStatus.error`) },
    };
    const item = config[status] || { bg: 'light', text: 'dark', label: status || '—' };
    return <Badge bg={item.bg} text={item.text}>{item.label}</Badge>;
};

const PolicyStatusBadge = ({ status }) => {
    const normalized = String(status || '').toLowerCase();
    const bg = normalized === 'cancelled' || normalized === 'annulé' || normalized === 'annule'
        ? 'secondary'
        : 'primary';
    return <Badge bg={bg}>{status || '—'}</Badge>;
};

const TripCancellationResult = ({ result, onDismiss, partner = TRIP_CANCELLATION_PARTNERS.FLOWER }) => {
    const { t } = useTranslation();
    const prefix = `pages.adminTripsPoliciesCancelledImport.${partner}`;

    const normalized = useMemo(() => normalizeTripCancellationResult(result), [result]);
    const [statusFilter, setStatusFilter] = useState('all');
    const [search, setSearch] = useState('');
    const [page, setPage] = useState(1);
    const [pageSize, setPageSize] = useState(20);
    const [exportError, setExportError] = useState(null);

    const details = useMemo(() => normalized?.details ?? [], [normalized]);
    const statusFilterKeys = useMemo(
        () => getTripCancellationStatusFilters(),
        []
    );

    const filteredDetails = useMemo(
        () => filterTripCancellationDetails(details, statusFilter, search),
        [details, statusFilter, search]
    );

    const policyRows = useMemo(
        () => flattenTripCancellationPolicyRows(filteredDetails),
        [filteredDetails]
    );

    /** Si la recherche porte sur une police, affiner après aplatissement. */
    const filteredPolicyRows = useMemo(
        () => filterTripCancellationPolicyRows(policyRows, 'all', search),
        [policyRows, search]
    );

    const pagination = useMemo(
        () => paginateTripCancellationDetails(filteredPolicyRows, page, pageSize),
        [filteredPolicyRows, page, pageSize]
    );

    const statusCounts = useMemo(
        () => buildTripCancellationStatusCounts(details),
        [details]
    );

    if (!normalized) return null;

    const { code, message, timestamp, summary, filters, error, success } = normalized;
    const variant = getTripCancellationAlertVariant(normalized, partner);
    const showSummary = shouldShowTripCancellationSummary(normalized);
    const isPartial = code === TRIP_CANCELLATION_CODES.PROCESSED
        && isTripCancellationPartialSuccess(normalized, partner);
    const showRowsSkipped = partner === TRIP_CANCELLATION_PARTNERS.ALOA;
    const showEmailsQueued = Number(summary?.emailsQueued) > 0;

    const BannerIcon = variant === 'success' ? FiCheckCircle
        : variant === 'info' ? FiInfo
            : variant === 'warning' ? FiAlertTriangle
                : FiXCircle;

    const codeTitleKey = `${prefix}.codes.${code}`;
    const codeTitle = t(codeTitleKey);
    const displayTitle = codeTitle !== codeTitleKey ? codeTitle : code;

    const translateRowStatus = (status) => {
        const key = `${prefix}.rowStatus.${status}`;
        const label = t(key);
        return label !== key ? label : (status || '—');
    };

    const handleExportExcel = () => {
        if (filteredPolicyRows.length === 0) {
            setExportError(t(`${prefix}.exportNoData`));
            return;
        }

        setExportError(null);
        const rows = buildTripCancellationExcelRows(filteredPolicyRows, {
            labels: {
                reservationNumber: t(`${prefix}.details.reservationNumber`),
                client: t(`${prefix}.details.client`),
                policyCode: t(`${prefix}.details.policyCode`),
                policyStatus: t(`${prefix}.details.policyStatus`),
                cancellationDate: t(`${prefix}.details.cancellationDate`),
                claimsUpdated: t(`${prefix}.details.claimsUpdated`),
                status: t(`${prefix}.details.status`),
                message: t(`${prefix}.details.message`),
            },
            translateStatus: translateRowStatus,
        });

        const worksheet = XLSX.utils.json_to_sheet(rows);
        const workbook = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(workbook, worksheet, 'Annulations');
        const stamp = new Date().toISOString().slice(0, 19).replace(/[-:T]/g, '').slice(0, 14);
        XLSX.writeFile(workbook, `trip_cancellations_${partner}_${stamp}.xlsx`);
    };

    return (
        <div className="triggered-recap-result trip-cancellation-result">
            <Alert variant={variant} className="triggered-recap-banner" dismissible={!!onDismiss} onClose={onDismiss}>
                <div className="d-flex align-items-start gap-2">
                    <BannerIcon size={22} className="mt-1 flex-shrink-0" aria-hidden />
                    <div className="flex-grow-1">
                        <Alert.Heading className="h5 mb-1">{displayTitle}</Alert.Heading>
                        <p className="mb-2">{message}</p>
                        {isPartial && (
                            <p className="mb-2 small">
                                <Badge bg="warning" text="dark">{t(`${prefix}.partialWarning`)}</Badge>
                            </p>
                        )}
                        {timestamp && (
                            <p className="mb-0 small text-muted">
                                {t(`${prefix}.timestamp`)} : {formatTripCancellationDateTime(timestamp)}
                            </p>
                        )}
                    </div>
                </div>
            </Alert>

            {!success && error && (
                <Alert variant={code === TRIP_CANCELLATION_CODES.INVALID ? 'warning' : 'danger'} className="mt-3">
                    {code === TRIP_CANCELLATION_CODES.INVALID && error.field === 'file' && (
                        <>
                            <p className="mb-1">{error.reason}</p>
                            {Array.isArray(error.missing_columns) && error.missing_columns.length > 0 && (
                                <p className="mb-0 trip-cancellation-missing-columns">
                                    {t(`${prefix}.missingColumns`)} : {error.missing_columns.join(', ')}
                                </p>
                            )}
                        </>
                    )}
                    {code === TRIP_CANCELLATION_CODES.INTERNAL && error.details && (
                        <p className="mb-0 triggered-recap-debug" title={String(error.details)}>
                            {t(`${prefix}.technicalHint`)} <code>{error.type || '—'}</code>
                        </p>
                    )}
                </Alert>
            )}

            {(filters.filename || filters.partner) && (
                <Card className="mb-3 mt-3">
                    <Card.Header className="d-flex align-items-center gap-2 bg-white">
                        <FiFilter />
                        <span className="fw-semibold">{t(`${prefix}.filtersTitle`)}</span>
                    </Card.Header>
                    <Card.Body>
                        <dl className="triggered-recap-filters-grid mb-0">
                            {filters.filename && (
                                <div className="triggered-recap-filter-item">
                                    <dt>{t(`${prefix}.filterFilename`)}</dt>
                                    <dd>{filters.filename}</dd>
                                </div>
                            )}
                            {filters.partner && (
                                <div className="triggered-recap-filter-item">
                                    <dt>{t(`${prefix}.filterPartner`)}</dt>
                                    <dd>{filters.partner}</dd>
                                </div>
                            )}
                        </dl>
                    </Card.Body>
                </Card>
            )}

            {showSummary && summary && (
                <Row className="g-3">
                    <Col xs={12} sm={6} xl={4} xxl={3}>
                        <StatCard icon={FiUsers} tone="primary" label={t(`${prefix}.summary.totalRows`)} value={summary.totalRows} />
                    </Col>
                    <Col xs={12} sm={6} xl={4} xxl={3}>
                        <StatCard icon={FiCheckCircle} tone="success" label={t(`${prefix}.summary.rowsProcessed`)} value={summary.rowsProcessed} />
                    </Col>
                    {showRowsSkipped && (
                        <Col xs={12} sm={6} xl={4} xxl={3}>
                            <StatCard icon={FiMinusCircle} tone="warning" label={t(`${prefix}.summary.rowsSkipped`)} value={summary.rowsSkipped} />
                        </Col>
                    )}
                    {summary.rowsNotFound > 0 && (
                        <Col xs={12} sm={6} xl={4} xxl={3}>
                            <StatCard icon={FiAlertTriangle} tone="warning" label={t(`${prefix}.summary.rowsNotFound`)} value={summary.rowsNotFound} />
                        </Col>
                    )}
                    {summary.rowsFailed > 0 && (
                        <Col xs={12} sm={6} xl={4} xxl={3}>
                            <StatCard icon={FiXCircle} tone="danger" label={t(`${prefix}.summary.rowsFailed`)} value={summary.rowsFailed} />
                        </Col>
                    )}
                    <Col xs={12} sm={6} xl={4} xxl={3}>
                        <StatCard icon={FiShield} tone="primary" label={t(`${prefix}.summary.policiesCancelled`)} value={summary.policiesCancelled} />
                    </Col>
                    <Col xs={12} sm={6} xl={4} xxl={3}>
                        <StatCard icon={FiXCircle} tone="danger" label={t(`${prefix}.summary.claimsRejected`)} value={summary.claimsRejected} />
                    </Col>
                    {showEmailsQueued && (
                        <Col xs={12} sm={6} xl={4} xxl={3}>
                            <StatCard icon={FiMail} tone="success" label={t(`${prefix}.summary.emailsQueued`)} value={summary.emailsQueued} />
                        </Col>
                    )}
                </Row>
            )}

            {showSummary && details.length > 0 && (
                <Card className="mt-4">
                    <Card.Header className="bg-white fw-semibold d-flex flex-wrap justify-content-between align-items-center gap-2">
                        <span>{t(`${prefix}.detailsTitle`)}</span>
                        <Button
                            variant="outline-success"
                            size="sm"
                            className="d-flex align-items-center"
                            onClick={handleExportExcel}
                        >
                            <FiDownload className="me-2" />
                            {t(`${prefix}.exportExcel`)}
                        </Button>
                    </Card.Header>
                    <Card.Body>
                        {exportError && (
                            <Alert variant="warning" className="mb-3" dismissible onClose={() => setExportError(null)}>
                                {exportError}
                            </Alert>
                        )}
                        <div className="trip-cancellation-details-toolbar">
                            <div className="d-flex flex-wrap gap-2">
                                <Nav variant="pills" className="trip-cancellation-status-tabs flex-wrap">
                                    {statusFilterKeys.map((key) => (
                                        <Nav.Item key={key}>
                                            <Nav.Link
                                                eventKey={key}
                                                active={statusFilter === key}
                                                onClick={() => {
                                                    setStatusFilter(key);
                                                    setPage(1);
                                                }}
                                                style={{ cursor: 'pointer' }}
                                            >
                                                {t(`${prefix}.statusFilter.${key}`)} ({statusCounts[key] ?? 0})
                                            </Nav.Link>
                                        </Nav.Item>
                                    ))}
                                </Nav>
                            </div>
                            <Form.Group style={{ minWidth: '220px', maxWidth: '320px' }}>
                                <Form.Label className="small mb-1">{t(`${prefix}.search`)}</Form.Label>
                                <div className="position-relative">
                                    <FiSearch className="position-absolute text-muted" style={{ left: 10, top: 10 }} />
                                    <Form.Control
                                        type="search"
                                        value={search}
                                        placeholder={t(`${prefix}.searchPlaceholder`)}
                                        onChange={(e) => {
                                            setSearch(e.target.value);
                                            setPage(1);
                                        }}
                                        style={{ paddingLeft: '2rem' }}
                                    />
                                </div>
                            </Form.Group>
                        </div>

                        {pagination.rows.length === 0 ? (
                            <div className="text-muted">{t(`${prefix}.noDetails`)}</div>
                        ) : (
                            <>
                                <div className="trip-cancellation-table-wrap">
                                    <Table striped hover responsive className="trip-cancellation-table mb-0">
                                        <thead>
                                            <tr>
                                                <th>{t(`${prefix}.details.reservationNumber`)}</th>
                                                <th>{t(`${prefix}.details.client`)}</th>
                                                <th>{t(`${prefix}.details.policyCode`)}</th>
                                                <th>{t(`${prefix}.details.policyStatus`)}</th>
                                                <th>{t(`${prefix}.details.cancellationDate`)}</th>
                                                <th>{t(`${prefix}.details.claimsUpdated`)}</th>
                                                <th>{t(`${prefix}.details.status`)}</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {pagination.rows.map((row, index) => (
                                                <tr key={`${row.reservationNumber}-${row.policyCode}-${index}`}>
                                                    <td><code>{row.reservationNumber || '—'}</code></td>
                                                    <td>{row.clientLabel || '—'}</td>
                                                    <td>{row.policyCode ? <code>{row.policyCode}</code> : '—'}</td>
                                                    <td><PolicyStatusBadge status={row.policyStatus} /></td>
                                                    <td>{formatTripCancellationDateTime(row.cancellationDate)}</td>
                                                    <td>{row.updatedClaimsCount ?? 0}</td>
                                                    <td><StatusBadge status={row.status} prefix={prefix} t={t} /></td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </Table>
                                </div>

                                <div className="trip-cancellation-pagination">
                                    <div className="d-flex align-items-center gap-2">
                                        <Form.Select
                                            size="sm"
                                            value={pageSize}
                                            style={{ width: 'auto' }}
                                            onChange={(e) => {
                                                setPageSize(Number(e.target.value));
                                                setPage(1);
                                            }}
                                        >
                                            {PAGE_SIZES.map((size) => (
                                                <option key={size} value={size}>
                                                    {t(`${prefix}.pageSizeOption`, { size })}
                                                </option>
                                            ))}
                                        </Form.Select>
                                        <span className="text-muted small">
                                            {t(`${prefix}.pageIndicator`, {
                                                from: pagination.total === 0 ? 0 : (pagination.page - 1) * pageSize + 1,
                                                to: Math.min(pagination.page * pageSize, pagination.total),
                                                total: pagination.total,
                                            })}
                                        </span>
                                    </div>
                                    <div className="d-flex gap-2">
                                        <Button
                                            variant="outline-secondary"
                                            size="sm"
                                            disabled={pagination.page <= 1}
                                            onClick={() => setPage((p) => p - 1)}
                                        >
                                            {t(`${prefix}.prev`)}
                                        </Button>
                                        <Button
                                            variant="outline-secondary"
                                            size="sm"
                                            disabled={pagination.page >= pagination.totalPages}
                                            onClick={() => setPage((p) => p + 1)}
                                        >
                                            {t(`${prefix}.next`)}
                                        </Button>
                                    </div>
                                </div>
                            </>
                        )}
                    </Card.Body>
                </Card>
            )}
        </div>
    );
};

export default TripCancellationResult;
